/**
 * Sort By filter component (accordion style).
 *
 * Displays sort options in an expandable accordion for the filter panel.
 * Uses radio buttons for single selection.
 */

'use client';

import { Icon } from '@/components/ui/Icon';
import type { SortOption } from '@/lib/algolia';
import { useSearchParams } from '@/lib/algolia/hooks/useSearchParams';
import { BRAND } from '@/lib/cms/config';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import styles from './SortFilter.module.scss';

interface SortFilterProps {
  currentSort?: SortOption;
}

const SORT_OPTIONS: { value: SortOption; labelKey: string }[] = [
  { value: 'relevance', labelKey: 'relevance' },
  { value: 'price_asc', labelKey: 'priceLowHigh' },
  { value: 'price_desc', labelKey: 'priceHighLow' },
  { value: 'newest', labelKey: 'newest' },
];

export function SortFilter({ currentSort = 'relevance' }: SortFilterProps) {
  const [isExpanded, setIsExpanded] = useState(false); // Start collapsed
  const { setSort } = useSearchParams();
  const t = useTranslations('plp');

  const handleSelect = (value: SortOption) => {
    setSort(value);
  };

  // Get localized label for current sort
  const currentLabel =
    SORT_OPTIONS.find((opt) => opt.value === currentSort)?.labelKey || 'relevance';

  return (
    <div className={`${styles.sortFilter} ${isExpanded ? styles.expanded : ''}`}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className={styles.name}>
          {BRAND === 'breitling' ? (
            <>
              {t('sortBy')}:{' '}
              <span className={styles.currentValue}>{t(`sort.${currentLabel}`)}</span>
            </>
          ) : (
            t('sortBy')
          )}
        </span>
        <Icon
          name="chevron-down"
          size={20}
          className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}
        />
      </button>

      {isExpanded && (
        <div className={styles.options}>
          {SORT_OPTIONS.map((option) => {
            const inputId = `sort-${option.value}`;
            const isActive = currentSort === option.value;

            return (
              <label
                key={option.value}
                htmlFor={inputId}
                className={`${styles.option} ${isActive ? styles.active : ''}`}
              >
                <input
                  type="radio"
                  id={inputId}
                  name="sort"
                  checked={isActive}
                  onChange={() => handleSelect(option.value)}
                  className={styles.radio}
                />
                <span className={styles.label}>{t(`sort.${option.labelKey}`)}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
